import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";

/**
 * `/esqueci-password` — request a password reset email.
 * Always shows a generic success message to prevent user enumeration.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-sm">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#f59e0b] uppercase tracking-widest font-semibold mb-8"
          data-testid="forgot-back-to-login"
        >
          <ArrowLeft className="w-3 h-3" /> Voltar ao login
        </Link>

        <p className="text-[10px] uppercase tracking-widest text-[#f59e0b] mb-3 font-bold">Recuperação</p>
        <h1 className="font-display text-4xl text-[#0a0f1e]">Esqueceu a palavra-passe?</h1>
        <p className="text-slate-500 mt-2 text-sm">
          Introduza o email da sua conta. Enviaremos um link para escolher uma nova palavra-passe.
        </p>

        {sent ? (
          <div className="mt-8 border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900" data-testid="forgot-success">
            <div className="flex items-start gap-3">
              <MailCheck className="w-5 h-5 mt-0.5 text-emerald-600" />
              <div>
                <div className="font-semibold text-emerald-900">Verifique o seu email</div>
                <p className="mt-1 text-emerald-800">
                  Se existir uma conta com esse email, enviámos um link de recuperação. O link é válido por 60 minutos.
                </p>
                <p className="mt-3 text-xs text-emerald-700">
                  Não recebeu? Verifique a pasta de SPAM ou{" "}
                  <button
                    type="button"
                    onClick={() => { setSent(false); }}
                    className="underline font-semibold"
                    data-testid="forgot-try-again"
                  >
                    tente novamente
                  </button>.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8" data-testid="forgot-form">
            <div>
              <Label className="label-caps mb-2 block" htmlFor="fp-email">Email</Label>
              <Input
                id="fp-email"
                data-testid="forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="cliente@ditours.pt"
                className="h-12 bg-transparent border-slate-200 rounded-none focus:border-[#f59e0b] text-[#0a0f1e]"
                required
              />
            </div>
            {err && (
              <div data-testid="forgot-error" className="mt-4 text-sm text-red-500 border-l-2 border-red-500 pl-3">
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="forgot-submit"
              className="mt-6 w-full h-12 rounded-none bg-[#f59e0b] hover:bg-[#d97706] text-[#0a0f1e] font-semibold tracking-wide"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar link de recuperação"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
